// Story 16.6 — cuentas, para el admin: generar un link de reset de contraseña
// (un solo uso, vence en un día) y desactivar o reactivar una cuenta.
// Desactivar corta sus sesiones, sus API keys y el WebSocket al instante.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useMe } from '@/hooks/queries/use-me';
import { api } from '@/lib/api-client';
import type { UserRole } from '@/lib/permissions';

interface UserItem {
  id: number;
  email: string;
  name: string | null;
  role: UserRole;
  disabledAt: string | null;
  createdAt: string;
}

const PLATFORM_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  support: 'Soporte',
  member: 'Cliente',
};

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function ResetLink(props: { email: string; path: string }): React.ReactElement {
  const url = `${window.location.origin}${props.path}`;
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      toast.error('No se pudo copiar: selecciona el link y cópialo a mano');
    }
  }

  return (
    <Card className="border-success/30 bg-success/5">
      <CardContent className="space-y-2 pt-6">
        <p className="text-sm">
          Link para que <strong>{props.email}</strong> elija una contraseña nueva. Sirve una vez,
          vence en 24 horas y no se vuelve a mostrar.
        </p>
        <div className="flex gap-2">
          <Input readOnly value={url} aria-label="Link de reset" className="font-mono text-xs" />
          <Button type="button" variant="outline" onClick={() => void copy()}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function UsersPage(): React.ReactElement {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserItem[]>('/api/v1/users'),
  });
  const [link, setLink] = useState<{ email: string; path: string } | null>(null);
  const reset = useMutation({
    mutationFn: (userId: number) =>
      api.post<{ path: string }>(`/api/v1/users/${userId}/password-reset`),
  });
  const setDisabled = useMutation({
    mutationFn: (v: { userId: number; disabled: boolean }) =>
      api.put(`/api/v1/users/${v.userId}`, { disabled: v.disabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  async function handleReset(user: UserItem): Promise<void> {
    try {
      const created = await reset.mutateAsync(user.id);
      setLink({ email: user.email, path: created.path });
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo generar el link'));
    }
  }

  async function handleToggle(user: UserItem): Promise<void> {
    const disable = !user.disabledAt;
    if (
      disable &&
      !window.confirm(
        `¿Desactivar a ${user.email}? Se cierran sus sesiones y se revocan sus API keys.`,
      )
    )
      return;
    try {
      await setDisabled.mutateAsync({ userId: user.id, disabled: disable });
      toast.success(disable ? `${user.email} desactivada` : `${user.email} reactivada`);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo cambiar la cuenta'));
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Usuarios</h1>
        <p className="text-sm text-muted-foreground">
          Las cuentas de la plataforma. A una organización se suman con una invitación.
        </p>
      </header>

      {link && <ResetLink email={link.email} path={link.path} />}

      {users.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando usuarios…</p>
      ) : users.isError ? (
        <p className="text-sm text-destructive">No se pudieron cargar los usuarios.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {(users.data ?? []).map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              <span className="flex-1">
                <span className="font-medium">{u.name ?? u.email}</span>
                {u.name && <span className="ml-2 text-muted-foreground">{u.email}</span>}
              </span>
              <span className="text-muted-foreground">{PLATFORM_ROLE_LABELS[u.role]}</span>
              {u.disabledAt && (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                  Desactivada
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={Boolean(u.disabledAt) || reset.isPending}
                onClick={() => void handleReset(u)}
              >
                Link de reset
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={u.id === me?.id || setDisabled.isPending}
                onClick={() => void handleToggle(u)}
              >
                {u.disabledAt ? 'Reactivar' : 'Desactivar'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
